import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BiImageAdd, BiXCircle, BiShow, BiHide } from "react-icons/bi";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { useRegisterMutation } from "../redux/auth/auth-api";
import { formAnimation } from "../utils/animationHelpers";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

const SignUp = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();
  const [registerMutation, { isLoading }] = useRegisterMutation();
  const [previewImg, setPreviewImg] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setValue("profile_image", file); // React Hook Form'un "setValue" fonksiyonu ile image state'i güncelleyebiliriz.

      // Create an image preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImg(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setValue("profile_image", null);
    setPreviewImg(null);
  };

  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("email", data.email);
    formData.append("password", data.password);
    if (data.profile_image) {
      formData.append("profile_image", data.profile_image, data.profile_image.name);
    }

    try {
      const response = await registerMutation(formData).unwrap();

      // Show success message from backend or fallback to translation
      toast.success(
        response.message ||
        intl.formatMessage({ id: 'auth.registrationSuccessCheckEmail' })
      );

      // Redirect to login page after showing toast message
      setTimeout(() => {
        navigate("/login");
      }, 2000); // 2 seconds delay to let user read the toast message
    } catch (error) {
      // API Fehler anzeigen - "Error:" Prefix entfernen
      let errorMessage = (error?.data?.message || error?.message || intl.formatMessage({ id: 'auth.registerError' })).replace(/^Error:\s*/i, '');
      
      // Spezifische Fehlermeldungen übersetzen
      if (errorMessage.toLowerCase().includes('email is registered') || errorMessage.toLowerCase().includes('email is already registered')) {
        errorMessage = intl.formatMessage({ id: 'auth.emailAlreadyRegistered' });
      }
      
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-cover bg-no-repeat bg-[position:0_-30vh] dark:bg-gray-900 px-[8%] lg:flex lg:items-center lg:justify-center lg:px-4"
         style={{ backgroundImage: "url('../assets/images/lightBlueBackground.png')" }}>
      <div className="lg:max-w-2xl lg:w-full">
        <h1 className="pt-8 pb-16 text-center text-gray-900 dark:text-white lg:text-5xl"><FormattedMessage id="auth.signup" /></h1>
        
        <motion.form
          {...formAnimation}
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white dark:bg-gray-800 shadow-[5px_5px_5px_5px_rgba(0,0,0,0.1)] dark:shadow-gray-900/50 rounded-[20px] pt-8 block"
        >
          <div className="px-[5%] pb-4 lg:px-[8%]">
            <label htmlFor="name" className="text-gray-500 dark:text-gray-400 font-medium text-xs block text-left pb-2 lg:text-sm">
              <FormattedMessage id="auth.name" />
            </label>
            <input
              type="text"
              {...register("name", 
                { required: intl.formatMessage({ id: 'validation.nameRequired' }),
                  pattern: {
                    value: /^(?=)(?=).{2,15}$/,
                    message: intl.formatMessage({ id: 'validation.nameInvalid' }),
                  },
                })}
              placeholder={intl.formatMessage({ id: 'auth.fullName' })}
              className="block text-left py-3 px-[4%] mb-2 w-full rounded-lg border border-[#dddddd] dark:border-gray-600 dark:bg-gray-700 dark:text-white lg:py-4 lg:text-base focus:outline-none focus:border-darkBlue dark:focus:border-blue-400"
            />
            {errors.name && <span className="text-red-500 text-xs">{errors.name.message}</span>}

            <label htmlFor="email" className="text-gray-500 dark:text-gray-400 font-medium text-xs block text-left pb-2 mt-2 lg:text-sm">
              <FormattedMessage id="auth.email" />
            </label>
            <input
              type="email"
              {...register("email", {
                required: intl.formatMessage({ id: 'validation.emailRequired' }),
                pattern: {
                  value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: intl.formatMessage({ id: 'validation.emailInvalid' }),
                },
              })}
              placeholder={intl.formatMessage({ id: 'auth.email' })}
              className="block text-left py-3 px-[4%] mb-2 w-full rounded-lg border border-[#dddddd] dark:border-gray-600 dark:bg-gray-700 dark:text-white lg:py-4 lg:text-base focus:outline-none focus:border-darkBlue dark:focus:border-blue-400"
            />
            {errors.email && <span className="text-red-500 text-xs">{errors.email.message}</span>}

            <label htmlFor="password" className="text-gray-500 dark:text-gray-400 font-medium text-xs block text-left pb-2 mt-2 lg:text-sm">
              <FormattedMessage id="auth.password" />
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...register("password", {
                  required: intl.formatMessage({ id: 'validation.passwordRequired' }),
                  minLength: {
                    value: 8,
                    message: intl.formatMessage({ id: 'validation.passwordMinLength' }),
                  },
                })}
                placeholder={intl.formatMessage({ id: 'auth.password' })}
                className="block text-left py-3 px-[4%] pr-12 mb-2 w-full rounded-lg border border-[#dddddd] dark:border-gray-600 dark:bg-gray-700 dark:text-white lg:py-4 lg:text-base focus:outline-none focus:border-darkBlue dark:focus:border-blue-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-darkBlue dark:hover:text-blue-400"
              >
                {showPassword ? <BiHide size={24} /> : <BiShow size={24} />}
              </button>
            </div>
            {errors.password && <span className="text-red-500 text-xs">{errors.password.message}</span>}

            <label htmlFor="picture" className="text-gray-500 dark:text-gray-400 font-medium text-xs block text-left pb-2 mt-4 lg:text-sm">
              <FormattedMessage id="auth.profilePicture" />
            </label>
            {previewImg ? (
              <div className="relative w-[150px] h-[150px] my-2 mx-auto text-center">
                <img src={previewImg} alt="Preview" className="w-full h-full rounded-full object-cover border-2 border-[#ddd]" />
                <BiXCircle 
                  className="absolute -top-2 -right-2 text-[#ff4d4d] bg-white rounded-full cursor-pointer hover:scale-125 transition-transform" 
                  size={24} 
                  onClick={removeImage} 
                />
              </div>
            ) : (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 border border-dashed border-gray-500 py-3 px-[4%] w-full rounded-lg mb-4 justify-center hover:border-darkBlue lg:text-base lg:py-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <BiImageAdd size={24} /> <FormattedMessage id="auth.addProfilePhoto" />
              </label>
            )}

            <button 
              type="submit" 
              disabled={isLoading}
              className="bg-gradient-blue border-none py-4 rounded-[50px] mb-4 text-white text-lg font-semibold w-full lg:text-xl lg:py-5 hover:bg-gradient-blue-reverse transition-all duration-300"
            >
              <FormattedMessage id="auth.register" />
              {isLoading && (
                <span className="spinner-border spinner-border-sm mx-1" role="status"></span>
              )}
            </button>
          </div>
        </motion.form>
        
        <p className="py-4 px-[2%] text-xs m-0 lg:text-sm lg:text-center">
          <FormattedMessage id="auth.haveAccount" />{" "}
          <Link to="/login" className="no-underline text-darkBlue hover:underline">
            <FormattedMessage id="auth.login" />
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignUp;
